<template>
    <div>
        <login-header />
        <div class="login">
            <div class="logo-icon">
                <div class="mr-re"></div>
                <div class="mr-bil"></div>
                <div class="mr-ly"></div>
            </div>
            <div class="tile stack-m">
                <div class="tile-title">
                    <h2>Reset Password</h2>
                </div>
                <div class="tile-content">
                    <template v-if="!passwordChanged && !invalidToken">
                        <p class="stack-m">
                            Enter and confirm a new password.
                        </p>
                        <r-input
                                label="Password"
                                v-model="form.newPassword"
                                :validate="$v.form.newPassword"
                                :password="true"
                                class="stack-l"
                                left-icon="lock"
                                @key-submit="submit"
                        />
                        <r-input
                                label="Confirm Password"
                                v-model="form.confirmPassword"
                                :validate="$v.form.confirmPassword"
                                :password="true"
                                class="stack-l"
                                left-icon="lock"
                                @key-submit="submit"
                        />
                        <r-button type="primary" :fluid="true" class="stack-m" @click="submit" :loading="isLoading">Reset Password</r-button>
                        <p class="text-center">
                            <router-link :to="{name: 'login'}" :disabled="isLoading">Return to Login</router-link>
                        </p>
                    </template>
                    <template v-else-if="invalidToken">
                        <p class="inset-m message message-negative">
                            Invalid reset password token.
                            <router-link :to="{name: 'forgot-password'}">Did you forget your password?</router-link>
                        </p>
                    </template>
                    <template v-else>
                        <p class="inset-m message">Your password has been reset.
                            <router-link :to="{name: 'login'}">Log in</router-link> now with your new password.</p>
                    </template>
                </div>
            </div>
            <v-error-messages
                :has-frontend-errors="$v.form.$error"
                :server-error="serverError"
                class="stack-m">
                <li class="list-item" v-if="!serverError && !$v.form.newPassword.required">New password required.</li>
                <li class="list-item" v-if="!serverError && !$v.form.newPassword.minLength">New password must be at least 8 characters long.
                </li>
                <li class="list-item" v-if="!serverError && !$v.form.newPassword.mustContainOneNumber">New password must contain
                    at least 1 digit.
                </li>
                <li class="list-item" v-if="!serverError && !$v.form.newPassword.mustContainLowerCase">New password must contain
                    at least 1 lowercase letter.
                </li>
                <li class="list-item" v-if="!serverError && !$v.form.confirmPassword.required">Confirm password required.</li>
                <li class="list-item" v-if="!serverError && !$v.form.confirmPassword.minLength">Confirm password must be at least
                    8 characters long.
                </li>
                <li class="list-item" v-if="!serverError && !$v.form.confirmPassword.sameAsPassword">Confirm password must be the
                    same as new password.
                </li>
            </v-error-messages>
        </div>
    </div>
</template>

<script>
    import LoginHeader from '@/modules/guest/login-header';
    import {vErrorMessages} from '@/components/form';
    import {required, minLength, sameAs} from 'vuelidate/lib/validators';

    export default {
        components: {
            LoginHeader, vErrorMessages,
        },
        props: {
            token: {
                type: [String, Number],
                default: null,
            },
        },
        data() {
            return {
                form: {
                    password: null,
                    confirmPassword: null,
                },
                isLoading: false,
                serverError: null,
                passwordChanged: false,
                invalidToken: false,
            };
        },
        validations: {
            form: {
                newPassword: {
                    required,
                    minLength: minLength(8),
                    mustContainOneNumber(pwd) {
                        return /\d/.test(pwd);
                    },
                    mustContainLowerCase(pwd) {
                        return /[a-z]/.test(pwd);
                    },
                },
                confirmPassword: {
                    required,
                    sameAs: sameAs('newPassword'),
                },
            },
        },
        methods: {
            async submit() {
                this.$v.$touch();
                if (!this.$v.form.$error) {
                    this.isLoading = true;
                    try {
                        const params = {
                            data: {newPassword: this.form.newPassword},
                            token: this.token,
                        };
                        const response = await this.$api(({users}) => users.resetPassword(params));
                        if (response.response.status === 201) {
                            this.passwordChanged = true;
                        }
                    } catch (error) {
                        const {status} = error;
                        if (status === 422 || status === 404) {
                            this.serverError = error;
                        }
                        this.isLoading = false;
                    }
                }
            },
        },
        async mounted() {
            try {
                this.isLoading = true;
                const {token} = this;
                const response = await this.$api(({users}) => users.getResetPasswordToken({token}));
                if (response.response.status === 200) {
                    this.invalidToken = false;
                    this.isLoading = false;
                }
            } catch (error) {
                const {status} = error;
                if (status === 404) {
                    this.invalidToken = true;
                }
                this.isLoading = false;
            }
        },
    };
</script>
